// URL utility functions for extracting data from legacy Aspirex secure URLs

/**
 * Extract the order key from a product selection secureUrl.
 * The URL format is: https://creators.aspireiq.com/orderRequests/{key}/...
 * The key is a base64-encoded JWT token used for PFA API authentication.
 *
 * @example
 * extractOrderKey("https://creators.aspireiq.com/orderRequests/eyJhbGciOi.../catalog")
 * // Returns "eyJhbGciOi..."
 */
export function extractOrderKey(secureUrl: string): string | null {
  try {
    const url = new URL(secureUrl);
    // Match /orderRequests/{key} pattern
    const match = url.pathname.match(/\/orderRequests\/([^/]+)/);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

/**
 * Extract the member request UUID from a secureUrl.
 * The URL format is: https://services.aspireiq-dev.com/app/{requestId}?...
 */
export function extractRequestId(secureUrl: string): string | null {
  try {
    const url = new URL(secureUrl);
    const match = url.pathname.match(/\/app\/([0-9a-f-]{36})/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Extract projectId and token from a secureUrl.
 * Supports multiple URL patterns:
 * - /project/{projectId}/... (terms - with trailing slash)
 * - /project/{projectId}?... (terms - without trailing slash)
 * - /external-post-project/{projectId}/...
 * - /creators/programs/{programId}/content/{externalPostProjectId}?token=... (content)
 */
export function extractProjectIdAndToken(secureUrl: string): {
  projectId: string | null;
  token: string | null;
} {
  try {
    const url = new URL(secureUrl);
    const token = url.searchParams.get("token");

    // Try multiple patterns for extracting projectId
    // Pattern 1: /project/{projectId}/ (terms - with trailing slash)
    // Pattern 2: /project/{projectId} (terms - at end of path or before query)
    // Pattern 3: /external-post-project/{projectId}
    // Pattern 4: /content/{externalPostProjectId} (content submission)
    const patterns = [
      /project\/([^/?]+)/,
      /external-post-project\/([^/?]+)/,
      /\/content\/([^/?]+)/,
    ];

    let projectId: string | null = null;
    for (const pattern of patterns) {
      const match = url.pathname.match(pattern);
      if (match?.[1]) {
        projectId = match[1];
        break;
      }
    }

    return { projectId, token };
  } catch {
    return { projectId: null, token: null };
  }
}
